import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Link, useHistory } from "react-router-dom";
import { settingsStoreData } from "Stores/settingsData";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import Utils from "Utils/utils";
import InboxUtils from "Utils/InboxUtils";
import { emailPreviewContentPopupState } from "Components/PreviewPopup/EmailPreviewContentPopup";
import { openCalendarPopup, openDialerCard } from "../commonUtils";
import { ReactComponent as SalesgearLogoIcon } from "./Icons/salesgearLogo.svg";
import { creditsIcon, actionIcons } from "./icons";
import { MemoizedProfile } from "./Profile";
import ShellTooltip from "Components/common/ShellTooltip";
import BulkJobNotification from "./BulkJobNotification";
import { BookDemoHeaderButton, CopilotHeaderButton, UpdatesHeaderButton } from "./HeaderAssist";
import "./styles.scss";
import userDetail from "Stores/userDetail";
import { creditUsagePopupState } from "Components/common/CreditsUsagePopup";
import CommandPaletteSearchInput from "Components/CommandPalette/CommandPaletteSearchInput";
import CommandPalette from "Components/CommandPalette/CommandPalette";
import commandPaletteStore from "Stores/CommandPaletteStore";
import { installLiveCredits } from "Stores/liveCredits";

const Header = observer(() => {
  const history = useHistory();
  const [updatedCredits, setUpdatedCredits] = useState(0);

  const openOneOffEmail = async () => {
    if (Utils.isAccountConnected()) {
      let fromEmail = InboxUtils.getPrimaryInboxEmail();
      if (fromEmail) {
        let bccEmail = [];
        let toEmail = "";
        if (
          settingsStoreData?.settingsData?.insertBcc &&
          settingsStoreData?.settingsData?.bccEmail?.length > 0
        ) {
          bccEmail.push({ email: settingsStoreData?.settingsData?.bccEmail });
        }
        let contentDom = Utils.insertSignature(fromEmail, true);
        let validatedEmailObject = {};
        let emailStatusResponse = await Utils.validateEmailAddressStatus([
          ...Utils.getEmailList({ bcc: bccEmail }),
          toEmail
        ]);
        if (emailStatusResponse && Object.keys(emailStatusResponse)?.length) {
          validatedEmailObject = Utils.addEmailStatus(
            {
              bcc: bccEmail || [],
              to: [{ email: toEmail }]
            },
            emailStatusResponse
          );
        }
        emailPreviewContentPopupState.setPopupValues({
          ...emailPreviewContentPopupState.popupValues,
          pageType: "globalHeader",
          isFromGlobalHeader: true
        });
        let emailDataObj = {
          ...emailPreviewContentPopupState.popupValues.emailData,
          from: fromEmail,
          ...validatedEmailObject,
          content: Utils.addEmptySpaceAboveThreadEmail(contentDom)
        };
        let localStateObj = {
          ...emailPreviewContentPopupState.editorInputValue.localState,
          showBccInputAddress: bccEmail?.length > 0,
          editorInsertCounter: 0
        };
        let tempObj = {
          emailType: "ONE_OFF_EMAIL",
          ...emailPreviewContentPopupState.popupValues,
          ...{ emailData: emailDataObj },
          ...{ localState: localStateObj }
        };
        emailPreviewContentPopupState.setEditorInputValue(tempObj);
        emailPreviewContentPopupState.setshowEditorComposePopup(true);
        emailPreviewContentPopupState.setShowPopup(true);
      }
    } else {
      Utils.showAddAccountPopup(
        { history },
        "Please connect your email account to send emails",
        () => emailPreviewContentPopupState.setResetPopup()
      );
    }
  };

  const handleHeaderIconsClick = (name = "") => {
    switch (name) {
      case "phoneCall": {
        Utils.mixpanelTrack(MXP_EVENT.CALL_CLICK_HEADER, {
          pageType: PAGE_TYPE.header
        });
        openDialerCard("globalHeader");
        break;
      }
      case "email": {
        Utils.mixpanelTrack(MXP_EVENT.EMAIL_CLICK_HEADER, {
          pageType: PAGE_TYPE.header
        });
        openOneOffEmail();
        break;
      }
      case "calendar": {
        Utils.mixpanelTrack(MXP_EVENT.CALENDER_CLICK_HEADER, {
          pageType: PAGE_TYPE.header
        });
        openCalendarPopup(history, "globalHeader");
        break;
      }
      case "creditsUsage": {
        Utils.mixpanelTrack(MXP_EVENT.PRICING_CLICK_HEADER, {
          pageType: PAGE_TYPE.header
        });
        history.push("/settings/creditUsage");
        break;
      }
      default: {
        break;
      }
    }
  };

  useEffect(() => {
    const { creditsEligible = 0, creditsUsed = 0 } =
      userDetail?.userFeatureFlag || {};

    const totalRemainingCredits = creditsEligible - creditsUsed || 0;
    settingsStoreData.setTotalRemainingCredits(totalRemainingCredits);
  }, [userDetail.userFeatureFlag]);

  // Spend anywhere updates the number without the hover (see liveCredits.js).
  useEffect(() => installLiveCredits(), []);

  useEffect(() => {
    const credits = Number(settingsStoreData.totalRemainingCredits || 0);
    setUpdatedCredits(credits.toLocaleString());
  }, [settingsStoreData.totalRemainingCredits]);

  useEffect(() => {
    const handleKeyDown = e => {
      const isMod = e.metaKey || e.ctrlKey;
      const key = (e.key || "").toLowerCase();
      if (isMod && key === "k") {
        e.preventDefault();
        if (commandPaletteStore.isOpen) {
          commandPaletteStore.close();
        } else {
          commandPaletteStore.open();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const renderHeaderIcon = item => {
    const { name = "", icon = <></>, title = "", custom = false } = item || {};
    // Credits keeps its own hover popup, so it gets no tooltip on top of it.
    return custom ? (
      <div
        key={name}
        className={`flexWrapper ${name} ${
          creditUsagePopupState.showPopup ? "hovered" : ""
        }`}
        onClick={() => handleHeaderIconsClick(name)}
        onMouseEnter={() => creditUsagePopupState.setShowPopup(true)}
        onMouseLeave={() => creditUsagePopupState.scheduleHide()}
      >
        <div className={`itemIcon ${name}`}>{icon}</div>
        <span className="customCreditsCount">{updatedCredits}</span>
      </div>
    ) : (
      <ShellTooltip label={title} key={name}>
        <div
          className={`itemIcon ${name}`}
          onClick={() => handleHeaderIconsClick(name)}
        >
          {icon}
        </div>
      </ShellTooltip>
    );
  };

  return (
    <header className="sgHeader">
      <div className="leftSection">
        <Link to="/">
          <div className="sideBarlogo">
            <SalesgearLogoIcon />
          </div>
        </Link>
        <CommandPaletteSearchInput />
        <CopilotHeaderButton />
      </div>
      {/* Credits, jobs, what's new, compose. Copilot sits beside search (row G);
          the right rail is gone on this branch. */}
      <div className="rightSection">
        <BookDemoHeaderButton />
        <span className="sgHeaderDivider" />
        {renderHeaderIcon(creditsIcon)}
        <span className="sgHeaderDivider" />
        <BulkJobNotification />
        <UpdatesHeaderButton />
        <span className="sgHeaderDivider" />
        {actionIcons.map(renderHeaderIcon)}
        <MemoizedProfile />
      </div>
      <CommandPalette
        onComposeEmail={openOneOffEmail}
        onOpenDialer={() => handleHeaderIconsClick("phoneCall")}
        onOpenCalendar={() => handleHeaderIconsClick("calendar")}
      />
    </header>
  );
});

export { Header };
export default Header;
